import React, { createContext, useContext } from "react";
import { useAuth0 } from "../auth/react-auth0-spa";
import { invokeApiOperation, encodeUriString } from "./common/ApiHelper";
import { agentFieldNames } from "../constants/model";

const AgentApiContext = createContext();
const HttpMethod = {
  Put: "PUT",
  Post: "POST",
  Get: "GET",
  Delete: "DELETE",
};

const limitMetadata = Object.freeze({
  min: 1,
  max: 200,
});

const DefaultLimit = 15;

export const AgentApiProvider = (props) => {
  const { getTokenSilently } = useAuth0();

  const fetchAgents = async (
    searchBy,
    searchValue,
    limit = DefaultLimit,
    continuationToken = null
  ) => {
    if (limit < limitMetadata.min || limit > limitMetadata.max)
      throw new Error("Invalid page size");

    const sanitizedSearchTerm = encodeUriString(searchValue);
    const sanitizedSearchParam =
      searchBy &&
      searchBy.length > 0 &&
      sanitizedSearchTerm &&
      sanitizedSearchTerm.length > 0
        ? `&${searchBy}=${sanitizedSearchTerm}`
        : "";
    const continuationTokenParam = getSanitizedContinuationTokenParam(
      continuationToken
    );

    let urlSuffix = `agents?limit=${limit}${sanitizedSearchParam}${continuationTokenParam}`;

    return await invokeApi(urlSuffix, HttpMethod.Get, true);

    // TODO: Append sort column?
  };

  const getSanitizedContinuationTokenParam = (token) => {
    return token && token.length > 0
      ? `&continuationToken=${encodeUriString(token)}`
      : "";
  };

  const activateAgent = async (agentId, comment) => {
    if (!agentId || agentId.length < 1)
      throw new Error("Agent identifier must be provided");

    let urlSuffix = `agents/${encodeUriString(agentId)}/activate`;

    return await invokeApi(urlSuffix, HttpMethod.Put, true, { comment });
  };

  const deactivateAgent = async (agentId, comment) => {
    if (!agentId || agentId.length < 1)
      throw new Error("Agent identifier must be provided");

    let urlSuffix = `agents/${encodeUriString(agentId)}/deactivate`;

    return await invokeApi(urlSuffix, HttpMethod.Put, true, { comment });
  };

  const assignRole = async (agentId, role, comment) => {
    if (!agentId || agentId.length < 1)
      throw new Error("Agent identifier must be provided");

    if (!role || role.length < 1) throw new Error("Role must be provided");

    let urlSuffix = `agents/${encodeUriString(agentId)}/assignRole`;

    return await invokeApi(urlSuffix, HttpMethod.Put, true, { role, comment });
  };

  const revokeAgentInvite = async (agentId, comment) => {
    if (!agentId || agentId.length < 1)
      throw new Error("Agent identifier must be provided");

    let urlSuffix = `invitations/${encodeUriString(agentId)}`;

    return await invokeApi(urlSuffix, HttpMethod.Delete, false, { comment });
  };

  const inviteAgent = async (email, role) => {
    return await invokeApi("invitations", HttpMethod.Post, true, {
      email,
      role,
      comment: "Agent invited",
    });
  };

  const getMyInfo = async () => {
    return await invokeApi(`agents/myInfo`, HttpMethod.Get, true);
  };

  const invokeApi = async (
    urlSuffix,
    httpMethod,
    parseResponseToJson,
    bodyData = null
  ) => {
    const token = await getTokenSilently();
    return await invokeApiOperation(
      token,
      urlSuffix,
      httpMethod,
      parseResponseToJson,
      bodyData
    );
  };

  const value = {
    getMyInfo: props.getMyInfo || getMyInfo,
    fetchAgents: props.fetchAgents || fetchAgents,
    inviteAgent: props.inviteAgent || inviteAgent,
    deactivateAgent: props.deactivateAgent || deactivateAgent,
    revokeAgentInvite: props.revokeAgentInvite || revokeAgentInvite,
    activateAgent: props.activateAgent || activateAgent,
    assignRole: props.assignRole || assignRole,
  };

  return (
    <AgentApiContext.Provider value={value}>
      {props.children}
    </AgentApiContext.Provider>
  );
};

export const useAgentApi = () => {
  return useContext(AgentApiContext);
};
